/**
 * CMR Update Service
 * 
 * Handles updates to patient Continuous Medical Records (CMR) with history tracking.
 * Used during SecureLink (check-in) process to:
 * - Update patient healthRecords
 * - Track name changes in akaNames array
 * - Track DOB changes in previousDOBs array
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';

// ============================================================================
// HISTORY TRACKING
// ============================================================================

/**
 * Add a name to the patient's AKA (Also Known As) history
 * @param {string} userId - User ID
 * @param {string} firstName - Previous first name
 * @param {string} lastName - Previous last name
 * @returns {Promise<void>}
 */
export async function addAkaName(userId, firstName, lastName) {
  if (!userId || !firstName || !lastName) {
    console.warn('[CMRUpdateService] Missing parameters for addAkaName');
    return;
  }

  const akaEntry = { firstName, lastName };
  
  await db.collection('users').doc(userId).update({
    akaNames: firebase.firestore.FieldValue.arrayUnion(akaEntry),
  });

}

/**
 * Add a DOB to the patient's previous DOBs history
 * @param {string} userId - User ID
 * @param {string} dob - Previous date of birth (ISO string)
 * @returns {Promise<void>}
 */
export async function addPreviousDOB(userId, dob) {
  if (!userId || !dob) {
    console.warn('[CMRUpdateService] Missing parameters for addPreviousDOB');
    return;
  }

  await db.collection('users').doc(userId).update({
    previousDOBs: firebase.firestore.FieldValue.arrayUnion(dob),
  });

}

// ============================================================================
// CMR UPDATE FUNCTIONS
// ============================================================================

/**
 * Update patient CMR (healthRecords) with optional history tracking
 * 
 * @param {string} userId - User ID
 * @param {Object} updates - Object containing field updates
 * @param {Object} [options] - Options for the update
 * @param {Object} [options.previousValues] - Previous values for history tracking
 * @param {boolean} [options.trackNameHistory] - Whether to track name changes in akaNames
 * @param {boolean} [options.trackDOBHistory] - Whether to track DOB changes in previousDOBs
 * @returns {Promise<void>}
 */
export async function updatePatientCMR(userId, updates, options) {
  if (!userId) {
    throw new Error('Missing required parameter: userId');
  }

  const {
    previousValues = {},
    trackNameHistory = true,
    trackDOBHistory = true,
  } = options ?? {};

  const batch = db.batch();
  const userRef = db.collection('users').doc(userId);

  // Track name history if name changed
  if (trackNameHistory && previousValues.firstName && previousValues.lastName) {
    const nameChanged = 
      (updates.firstName && updates.firstName !== previousValues.firstName) ||
      (updates.lastName && updates.lastName !== previousValues.lastName);

    if (nameChanged) {
      // Add old name to AKA history
      batch.update(userRef, {
        akaNames: firebase.firestore.FieldValue.arrayUnion({
          firstName: previousValues.firstName,
          lastName: previousValues.lastName,
        }),
      });
    }
  }

  // Track DOB history if DOB changed
  if (trackDOBHistory && previousValues.dateOfBirth) {
    const dobChanged = updates.dateOfBirth && updates.dateOfBirth !== previousValues.dateOfBirth;

    if (dobChanged) {
      // Add old DOB to history
      batch.update(userRef, {
        previousDOBs: firebase.firestore.FieldValue.arrayUnion(previousValues.dateOfBirth),
      });
    }
  }

  // Build healthRecords update object
  const healthRecordsUpdate = {};
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) {
      healthRecordsUpdate[`healthRecords.${key}`] = value;
    }
  });

  if (Object.keys(healthRecordsUpdate).length > 0) {
    healthRecordsUpdate['healthRecords.updatedAt'] = firebase.firestore.FieldValue.serverTimestamp();
    batch.update(userRef, healthRecordsUpdate);
  }

  await batch.commit();
}

/**
 * Update specific healthRecords fields without history tracking
 * Simpler version for non-critical field updates
 * 
 * @param {string} userId - User ID
 * @param {Object} updates - Object containing field updates (will be nested under healthRecords)
 * @returns {Promise<void>}
 */
export async function updateHealthRecords(userId, updates) {
  if (!userId) {
    throw new Error('Missing required parameter: userId');
  }

  const healthRecordsUpdate = {};
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) {
      healthRecordsUpdate[`healthRecords.${key}`] = value;
    }
  });

  if (Object.keys(healthRecordsUpdate).length === 0) {
    return;
  }

  healthRecordsUpdate['healthRecords.updatedAt'] = firebase.firestore.FieldValue.serverTimestamp();

  await db.collection('users').doc(userId).update(healthRecordsUpdate);
}

// ============================================================================
// CMR READ FUNCTIONS
// ============================================================================

/**
 * Get patient CMR data including history (akaNames, previousDOBs)
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Patient CMR data with history, or null if not found
 */
export async function getPatientCMRWithHistory(userId) {
  if (!userId) {
    console.warn('[CMRUpdateService] No userId provided for getPatientCMRWithHistory');
    return null;
  }

  try {
    const doc = await db.collection('users').doc(userId).get();

    if (!doc.exists) {
      return null;
    }

    const data = doc.data();

    return {
      // Health records (CMR data)
      healthRecords: data.healthRecords || {},
      
      // History arrays
      akaNames: data.akaNames || [],
      previousDOBs: data.previousDOBs || [],
      
      // Legacy fields that might also contain name/DOB
      name: data.name,
      lastName: data.lastName,
      dob: data.dob,
      
      // User metadata
      uid: data.uid,
      email: data.email,
      phone: data.phone,
    };
  } catch (error) {
    console.error('[CMRUpdateService] Error fetching patient CMR:', error);
    return null;
  }
}

/**
 * Check if patient has existing DOB (for first-entry confirmation logic)
 * 
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} True if patient has a DOB set
 */
export async function patientHasDOB(userId) {
  if (!userId) {
    return false;
  }

  try {
    const doc = await db.collection('users').doc(userId).get();

    if (!doc.exists) {
      return false;
    }

    const data = doc.data();
    const healthRecordsDOB = data.healthRecords?.dateOfBirth;
    const legacyDOB = data.dob;

    return !!(healthRecordsDOB || legacyDOB);
  } catch (error) {
    console.error('[CMRUpdateService] Error checking patient DOB:', error);
    return false;
  }
}

/**
 * Get normalized patient data (handles both healthRecords and legacy fields)
 * Useful for check-in form to display consistent data
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Normalized patient data
 */
export async function getNormalizedPatientData(userId) {
  const cmrData = await getPatientCMRWithHistory(userId);
  
  if (!cmrData) {
    return null;
  }

  const hr = cmrData.healthRecords || {};

  return {
    // Personal info - prefer healthRecords, fall back to legacy
    firstName: hr.firstName || cmrData.name?.split(' ')[0] || '',
    lastName: hr.lastName || cmrData.lastName || cmrData.name?.split(' ').slice(1).join(' ') || '',
    dateOfBirth: hr.dateOfBirth || cmrData.dob || null,
    sex: hr.sex || hr.gender || null,
    
    // Contact info
    email: hr.email || cmrData.email || null,
    phone: hr.phone || cmrData.phone || null,
    address: hr.address || null,
    emergencyContact: hr.emergencyContact || null,
    
    // Medical info
    medications: hr.medications || [],
    allergies: hr.allergies || [],
    pmh: hr.pmh || hr.pastMedicalHistory || [],
    psh: hr.psh || hr.pastSurgicalHistory || [],
    familyHistory: hr.familyHistory || [],
    
    // Insurance
    insurance: hr.insurance || null,
    
    // History
    akaNames: cmrData.akaNames || [],
    previousDOBs: cmrData.previousDOBs || [],
    
    // Metadata
    uid: cmrData.uid,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  addAkaName,
  addPreviousDOB,
  updatePatientCMR,
  updateHealthRecords,
  getPatientCMRWithHistory,
  patientHasDOB,
  getNormalizedPatientData,
};
